// useGenerateTexts.ts
import { useMemo } from 'react';
import { PageSEO } from '~/data/types/car/cars';
import { ResultPageSEO } from '~/data/types/resultPageType';
import { ISeoResutlsPage } from '~/data/types/seo/seoResutlsPages';
import { useAppSelector } from '~/redux-toolkit/store/hooks';
import { RootState } from '~/redux-toolkit/store/store';

type BrandName = {
  brand?: string;
  model?: string;
};

type DefaultTexts = {
  pageTitle: string;
  canonicalLink: string;
  pageTitleMeta: string;
  pageDescriptionMeta: string;
  aria: string;
  id: string;
};

export type SearchType = 'new' | 'private-leasing' | 'used';

interface TextEntries {
  [key: string]: string;
}

function replaceTexts(texts: TextEntries, brandName: BrandName): TextEntries {
  const result: TextEntries = {};
  Object.keys(texts)?.forEach((key) => {
    const value = texts[key];
    if (typeof value === 'string') {
      result[key] = value
        .replace('{יצרן}', brandName?.brand || '')
        .replace('{דגם}', brandName?.model || '')
        .trim()
        .replaceAll('  ', ' ');
    } else {
      result[key] = value;
    }
  });
  return result;
}

const valueTexts = {
  new: {
    brandTitle: '{יצרן} {דגם} חדשה למכירה',
    brandMetaTitle: 'מבחר רכבי {יצרן} חדשים למכירה | freesbe',
    modalMetaTitle: '{יצרן} {דגם} חדשה למכירה | freesbe',
    brandMetaDescription:
      'המכוניות של חלומותיך מעבר לפינה. מבחר ענק של מכוניות {יצרן} חדשות לקנייה ולליסינג  במיוחד בשבילך.',
    modalMeteDescription:
      'המכוניות של חלומותיך מעבר לפינה. דגמי {יצרן} {דגם} חדשים לקנייה ולליסינג  במיוחד בשבילך.',
  },
  'private-leasing': {
    brandTitle: '{יצרן} {דגם} בליסינג פרטי',
    brandMetaTitle: 'מבחר רכבי {יצרן} בליסינג פרטי | freesbe',
    modalMetaTitle: '{יצרן} {דגם} בליסינג פרטי | freesbe',
    brandMetaDescription:
      'המכוניות של חלומותיך מעבר לפינה. מבחר ענק של מכוניות {יצרן} חדשות לקנייה ולליסינג  במיוחד בשבילך.',
    modalMeteDescription:
      'המכוניות של חלומותיך מעבר לפינה. דגמי {יצרן} {דגם} חדשים לקנייה ולליסינג  במיוחד בשבילך.',
  },
  used: {
    brandTitle: '{יצרן} {דגם} יד שנייה למכירה',
    brandMetaTitle: 'מבחר רכבי {יצרן} יד שנייה למכירה | freesbe',
    modalMetaTitle: 'מבחר דגמי {יצרן} {דגם} יד שנייה למכירה | freesbe',
    brandMetaDescription:
      'המכוניות של חלומותיך מעבר לפינה. מבחר ענק של מכוניות {יצרן} יד שניה למכירה ולטרייד אין במיוחד בשבילך.',
    modalMeteDescription:
      'המכוניות של חלומותיך מעבר לפינה. מבחר ענק של דגמי {יצרן} {דגם} יד שניה למכירה ולטרייד אין במיוחד בשבילך.',
  },
};

export const useGenerateSeoTexts = (
  brandName: BrandName,
  defaultTexts: DefaultTexts,
  searchType: SearchType,
  query?: {
    filters?: { models?: string[]; brands?: string[]; categories?: string[] };
  },
  seoContent?: ISeoResutlsPage,
) => {
  const { options: newCarOptions, cars: newCars } = useAppSelector(
    (state: RootState) => state.resultPage,
  );
  const { options: leasedOptions, cars: leasedCars } = useAppSelector(
    (state: RootState) => state.resultPageLease,
  );
  const { options: usedCarOptions, cars: usedCars } = useAppSelector(
    (state: RootState) => state.usedCarFilters,
  );

  return useMemo(() => {
    const { pageTitle, pageTitleMeta, pageDescriptionMeta } = defaultTexts;
    const { models = [], brands = [], categories = [] } = query?.filters || {};

    const singleCategory = categories.length === 1;
    const onlyBrand = brands.length === 1 && models.length === 0;
    const brandAndTwoModels = brands.length === 1 && models.length === 2;
    const brandAndModel = brands.length === 1 && models.length === 1;

    if (onlyBrand || brandAndTwoModels) {
      const strapiSEOBrandData = seoContent?.seoManufacturerContent?.find(
        (manu) => manu.brand.toLowerCase() === brands?.[0].toLowerCase(),
      );

      const interpolatedTexts = replaceTexts(valueTexts[searchType], {
        brand: brandName.brand || '',
        model: '',
      });

      const { brandTitle, brandMetaTitle, brandMetaDescription } =
        interpolatedTexts;

      return {
        title: brandTitle,
        metaTitle: strapiSEOBrandData?.resultPageMetaTitle || brandMetaTitle,
        metaDescription:
          strapiSEOBrandData?.resultPageMetaDescription || brandMetaDescription,
        metaKeywords: strapiSEOBrandData?.resultPageKeywords,
      };
    }

    if (brandAndModel) {
      const carsMap = {
        new: newCars,
        'private-leasing': leasedCars,
        used: usedCars,
      };

      const cars = carsMap[searchType]?.[0];

      const seoData = cars?.seo ?? ({} as PageSEO);

      const interpolatedTexts = replaceTexts(valueTexts[searchType], {
        brand: brandName.brand || '',
        model: brandName.model || '',
      });

      const { brandTitle, modalMetaTitle, modalMeteDescription } =
        interpolatedTexts;

      return {
        title: brandTitle,
        metaTitle: seoData.resultPageMetaTitle || modalMetaTitle,
        metaDescription:
          seoData.resultPageMetaDescription || modalMeteDescription,
        metaKeywords: seoData.resultPageKeywords,
      };
    }

    if (singleCategory) {
      const categoryOptionsMap = {
        new: newCarOptions.categories.values,
        'private-leasing': leasedOptions.categories.values,
        used: usedCarOptions.categories.values,
      };

      const chosenCategory = categoryOptionsMap[searchType]?.find(
        (cat) => cat.title === categories[0],
      );

      const seoData =
        chosenCategory?.seo?.page?.find(
          (page) => page.searchType === searchType,
        ) ?? ({} as ResultPageSEO);

      return {
        title: seoData.resultPageTitle || pageTitle,
        metaTitle: seoData.metaTitle || pageTitleMeta,
        metaDescription: seoData.metaDescription || pageDescriptionMeta,
      };
    }

    return {
      title: pageTitle,
      metaTitle: pageTitleMeta,
      metaDescription: pageDescriptionMeta,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    defaultTexts,
    query?.filters,
    seoContent?.seoManufacturerContent,
    searchType,
    brandName.brand,
    brandName.model,
    newCars,
    leasedCars,
    usedCars,
    newCarOptions.categories.values,
    leasedOptions.categories.values,
    usedCarOptions.categories.values,
  ]);
};
